import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import auth from '../../auth';
import { observe } from '../../lib/intersectionObserver';

const cacheBuster = Date.now().toString(36);

const postMessageToIframe = ({target, message, origin}) => {
  if (target && target.contentWindow) {
    target.contentWindow.postMessage(message, origin);
  }
};

/**
 * Must only initialize iframe when all of the required config is available
 * This could do with a refactor to make it simpler to understand
 */
const ViziaComponentIframe = (props) => {
  const {
    id = null,
    colors = {},
    folderOptions = null,
    slide_id = null,
    name,
    options = {},
    component_type_id,
    serverside = false,
    isInteractive = true,
    config = {},
    otp = null,
    shouldLoadIframe: shouldLoadIframeFromProps = false,
    preloadState: preloadStateFromProps = null,
    uiMode = null
  } = props;

  const bundleLoaderUrlOrigin = config.bundleLoaderUrl && config.bundleLoaderUrl.origin;
  const bundleLoaderUrlHref = config.bundleLoaderUrl && config.bundleLoaderUrl.href;

  const [shouldLoadIframeFromState, setShouldLoadIframeFromState] = useState(false);
  const [preloadStateFromState, setPreloadStateFromState] = useState(null);

  const nodeRef = useRef(null);
  const unobserveIntersection = useRef(null);
  const hasInitialized = useRef(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const preloadState = preloadStateFromState || preloadStateFromProps;
  const shouldLoadIframe = !!preloadState && (shouldLoadIframeFromState || shouldLoadIframeFromProps);

  const iframeUrl = useMemo(() => {
    if (!component_type_id) {
      return null;
    }

    const url = new URL(bundleLoaderUrlHref);

    url.searchParams.set('bundle', component_type_id);
    url.searchParams.set('cachebuster', cacheBuster);

    if (otp) {
      url.searchParams.set('otp', otp);
    }

    url.searchParams.set('preloading', true);

    return url.href;
  }, [bundleLoaderUrlHref, otp, component_type_id]);

  const getComponentConfig = useCallback((profile) => ({
    is_public: typeof id === 'string',
    colors: {
      palette: colors.palette,
      query: colors.query,
      category: colors.category
    },
    nsfw: folderOptions.nsfw,
    locale: folderOptions.locale,
    themeId: folderOptions.themeId,
    theme: folderOptions.theme,
    timezone: folderOptions.timezone,
    scene: {
      id,
      slide_id,
      name,
      options,
      component_type_id,
      type: component_type_id,
      serverside
    },
    bwApiRootUrl: config.bwApiRootUrl && config.bwApiRootUrl.href,
    imagecheckRootUrl: config.imageCheckRootUrl && config.imageCheckRootUrl.href,
    token: profile.api2AccessToken,
    isInteractive
  }), [
    id,
    colors,
    folderOptions,
    slide_id,
    name,
    options,
    component_type_id,
    serverside,
    config,
    isInteractive
  ]);

  const initializeIframe = useCallback((profile) => {
    const componentConfig = getComponentConfig(profile);

    postMessageToIframe({
      target: nodeRef.current,
      message: {
        config: componentConfig,
        preloadState
      },
      origin: bundleLoaderUrlOrigin
    });

    hasInitialized.current = true;
  }, [getComponentConfig, preloadState, bundleLoaderUrlOrigin]);

  const onIframeLoad = useCallback(() => {
    setHasLoaded(true);
  }, []);

  const onIframeNode = useCallback((node) => {
    if (node && !nodeRef.current) {
      nodeRef.current = node;
      nodeRef.current.addEventListener('load', onIframeLoad);

      if (uiMode === 'scroll') {
        unobserveIntersection.current = observe(nodeRef.current, () => {
          setShouldLoadIframeFromState(true);
          setPreloadStateFromState('bundle_start');
        });
      }
    } else if (!node && nodeRef.current) {
      unobserveIntersection.current && unobserveIntersection.current();
      nodeRef.current = null;
    }
  }, [onIframeLoad]);

  useEffect(() => {
    if ([
      hasLoaded,
      !hasInitialized.current,
      folderOptions,
      !colors.isPaletteLoading,
      !colors.isColorsLoading
    ].every((condition) => condition)) {
      if (typeof id === 'string' || !auth.hasStore()) {
        Promise.resolve().then(() => initializeIframe({}));
      } else {
        auth.getUserProfile().then(initializeIframe);
      }
    }
  }, [
    id,
    folderOptions,
    colors,
    initializeIframe,
    hasLoaded
  ]);

  useEffect(() => {
    if (hasInitialized.current) {
      postMessageToIframe({
        target: nodeRef.current,
        message: {preloadState},
        origin: bundleLoaderUrlOrigin
      });
    }
  }, [preloadState, bundleLoaderUrlOrigin, id]);

  if (!(shouldLoadIframe && iframeUrl)) {
    return null;
  }

  return (
    <iframe
      src={iframeUrl}
      className="ViziaComponent__content"
      ref={onIframeNode}
    />
  );
};

ViziaComponentIframe.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  name: PropTypes.string,
  component_type_id: PropTypes.string,
  slide_id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  folderOptions: PropTypes.object,
  options: PropTypes.object,
  config: PropTypes.object,
  serverside: PropTypes.any,
  isInteractive: PropTypes.bool,
  otp: PropTypes.string,
  shouldLoadIframe: PropTypes.bool,
  preloadState: PropTypes.string,
  uiMode: PropTypes.string
};

export default ViziaComponentIframe;
